import React from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import PointsFaqSection from '../../../components/marketing/PointsFaqSection'
import { useAuthHelper } from '../../../hooks/useAuthHelper'
import { ROUTES } from '../../../helpers/routes-helper'
import { ensureAnonId } from '../../../helpers/anon-id'
import {
  fetchAnonProvisionalPoints,
  fetchWalletBalance,
  WalletBalanceResponse,
} from '../../../services/rewardsService'
import { setPendingPointsClaimIntent } from '../../../helpers/pointsClaimIntent'
import { errorLog } from '../../../utils/log-helper'
import { logEvent } from '../../../analytics/analytics'

const DocumentTitle = 'Resactly | Points & Rewards'

type LoadState = 'loading' | 'ready' | 'error'

/**
 * Rewards page — reframed around OWNERSHIP (2026-07). For an anonymous device it reads the real
 * provisional points this device has already earned (saves, scans, views) and frames signup as
 * *claiming what's yours*, not creating an account. For a signed-in eater it shows their live
 * balance. Self-contained editorial design scoped under `.rsly-rewards` so its type/token system
 * never leaks into the app's MUI theme (mirrors the About page).
 */
const RewardsInfoPage: React.FC = () => {
  const { loginAuthUser } = useAuthHelper()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth0()

  // The real number driving the hero count-up: provisional points for anon devices (the ownership
  // lever), this month's qualifier progress for signed-in eaters. Null while loading / on error.
  const [points, setPoints] = React.useState<number | null>(null)
  const [state, setState] = React.useState<LoadState>('loading')
  // Full wallet balance for the authed hero — the reward mechanic is monthly, so the signed-in hero
  // leads with this month's qualifier progress and keeps the lifetime balance as a secondary line.
  const [wallet, setWallet] = React.useState<WalletBalanceResponse | null>(null)

  React.useEffect(() => {
    // Wait until Auth0 has resolved before picking the provisional (anon) vs wallet (authed)
    // source. During token resolution on navigation Auth0 briefly reports isAuthenticated=false;
    // fetching the anon number then and flipping to the wallet balance on the next render is what
    // made the hero value jump and re-run its count-up. Gating on authLoading keeps it stable.
    if (authLoading) {
      setState('loading')
      return
    }
    let active = true
    setState('loading')
    void (async () => {
      try {
        if (isAuthenticated) {
          const balance = await fetchWalletBalance()
          if (!active) return
          setWallet(balance)
          // The count-up now animates this month's qualifier progress, not the all-time balance.
          const monthly = balance.monthlyProgressPoints
          setPoints(typeof monthly === 'number' ? monthly : 0)
          setState('ready')
        } else {
          const anonId = await ensureAnonId()
          if (!anonId) {
            if (active) setState('error')
            return
          }
          const res = await fetchAnonProvisionalPoints(anonId)
          if (!active) return
          setPoints(res.provisionalPoints)
          setState('ready')
        }
      } catch (error) {
        // Non-critical: the page still renders its ownership messaging without the live number.
        errorLog('[RewardsInfoPage] failed to load points', error)
        if (active) setState('error')
      }
    })()
    return () => {
      active = false
    }
  }, [isAuthenticated, authLoading])

  // Only show the big-number ownership hero when we actually have a positive number to own (anon).
  const hasNumber = state === 'ready' && points != null && points > 0
  const showAnonPointsLever = !isAuthenticated && hasNumber
  // The authed monthly hero shows even at 0 progress ("0 of Y") — starting the month is the point.
  const authedReady = isAuthenticated && state === 'ready'

  // Count-up animation for the hero number. Runs once the real value lands; respects reduced-motion.
  const [displayed, setDisplayed] = React.useState(0)
  React.useEffect(() => {
    if ((!hasNumber && !authedReady) || points == null) {
      setDisplayed(0)
      return
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      setDisplayed(points)
      return
    }
    let raf = 0
    const duration = 1100
    const start = performance.now()
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplayed(Math.round(points * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [hasNumber, authedReady, points])

  // Rewards funnel: fire rewards_gate_shown once when the anon provisional-points hero renders
  // (the "claim your points" lever). One-shot per view, guarded like home_loaded.
  const rewardsGateShownRef = React.useRef(false)
  React.useEffect(() => {
    if (showAnonPointsLever && !rewardsGateShownRef.current) {
      rewardsGateShownRef.current = true
      logEvent('rewards_gate_shown', {
        reason: 'points',
        screen_name: 'rewards',
      })
    }
  }, [showAnonPointsLever])

  // Authed monthly-hero values. The reward is monthly: earn -> hit this month's goal -> unlock this
  // month's credit. `displayed` (the count-up) tracks monthlyEarned; the lifetime balance is secondary.
  const monthlyEarned = wallet?.monthlyProgressPoints ?? 0
  const monthlyGoal = wallet?.monthlyGoalPoints ?? null
  const monthlyPct =
    monthlyGoal && monthlyGoal > 0 ? Math.min(1, monthlyEarned / monthlyGoal) : 0
  const monthlyRemaining = monthlyGoal ? Math.max(0, monthlyGoal - monthlyEarned) : 0
  const monthlyQualified = !!(monthlyGoal && monthlyEarned >= monthlyGoal)
  const bankedPoints = wallet?.pointsBalance ?? 0
  const redeemableCents = wallet?.walletAvailableCents ?? 0

  const handleCTA = (): void => {
    // The primary claim lever: gate_shown -> tap -> signup is measurable. rewards_cta_tapped fires
    // on both branches; login_prompted{gate:'rewards'} instruments the logged-out signup gate.
    logEvent('rewards_cta_tapped', {
      isLoggedIn: isAuthenticated,
      destination: 'POINTS',
      screen_name: 'rewards',
    })
    if (isAuthenticated) {
      navigate(ROUTES.POINTS)
    } else {
      logEvent('login_prompted', {
        gate: 'rewards',
        screen_name: 'rewards',
      })
      // Stash the claim intent so BaseLayout resumes on the points page and skips onboarding
      // (mirrors the save-restaurant / menu-scan anon->signup resume). The claim runs on signup.
      setPendingPointsClaimIntent({ returnRoute: ROUTES.POINTS })
      loginAuthUser()
    }
  }

  const ctaLabel = isAuthenticated
    ? 'Go to my points'
    : showAnonPointsLever
      ? `Claim my ${points?.toLocaleString()} points`
      : 'Create an account'

  return (
    <div className="rsly-rewards">
      <Helmet>
        <title>{DocumentTitle}</title>
        <meta
          name="description"
          content="You've already earned points on Resactly. Restaurants pay to understand how dining decisions get made, and a share of that value comes back to you."
        />
      </Helmet>

      <style>{css}</style>

      <header className="rw-hero">
        <div className="rw-wrap">
          <p className="rw-eyebrow">{isAuthenticated ? 'This month’s credit' : 'Points you already own'}</p>

          {authedReady ? (
            /* Authed hero: the reward is monthly, so lead with this month's qualifier progress —
               not the all-time balance (which never resets and doesn't say if you've qualified). */
            <>
              <h1>
                {monthlyQualified ? (
                  <>This month’s credit is <span className="warm">yours.</span></>
                ) : (
                  <>Your progress <span className="warm">this month.</span></>
                )}
              </h1>
              <div className="rw-count" aria-live="polite">
                <span className="num">{displayed.toLocaleString()}</span>
                <span className="unit">
                  {monthlyGoal ? `of ${monthlyGoal.toLocaleString()} pts` : 'points this month'}
                </span>
              </div>
              {monthlyGoal ? (
                <div className="rw-prog">
                  <div className="track">
                    <i style={{ width: `${Math.round(monthlyPct * 100)}%` }} />
                  </div>
                  <p className="cap">
                    {monthlyQualified
                      ? "You've hit this month's credit 🎉"
                      : `${monthlyRemaining.toLocaleString()} points to go toward this month's credit`}
                  </p>
                </div>
              ) : null}
              <p className="rw-sub">
                {bankedPoints > 0 || redeemableCents > 0
                  ? `${bankedPoints.toLocaleString()} points banked · ${formatCredit(redeemableCents)} ready to redeem. Hit your monthly goal to unlock each month's credit.`
                  : "Earn points on your dining activity and hit your monthly goal to unlock this month's credit."}
              </p>
            </>
          ) : hasNumber ? (
            <>
              <h1>
                You&apos;ve already earned <span className="warm">points.</span>
              </h1>
              <div className="rw-count" aria-live="polite">
                <span className="num">{displayed.toLocaleString()}</span>
                <span className="unit">points</span>
              </div>
              <p className="rw-sub">
                Your activity on this device has already earned these. Sign up to keep them — they
                credit to your balance the moment you do.
              </p>
            </>
          ) : (
            <>
              <h1>
                Rewards you <span className="warm">actually own.</span>
              </h1>
              <p className="rw-sub">
                Restaurants pay to understand how dining decisions get made. A share of that value
                comes back to you as points — no ads, no reviews sold, no strings.
              </p>
            </>
          )}

          <div className="rw-paths">
            <button type="button" className="rw-btn rw-primary" onClick={handleCTA}>
              {ctaLabel}
            </button>
          </div>
          {!isAuthenticated && (
            <p className="rw-fine">
              {showAnonPointsLever
                ? 'Points credit to your balance when you create an account.'
                : "Create an account to start earning and redeeming."}
            </p>
          )}
        </div>
      </header>

      <section>
        <div className="rw-wrap">
          <p className="rw-eyebrow">Why you have points</p>
          <h2>You earned these by doing what you already do.</h2>
          <p className="rw-lead">
            Every move you make while deciding where to eat is worth something. Here&apos;s where your
            points come from.
          </p>
          <div className="rw-earn">
            {EARN.map((e) => (
              <div className="rw-card" key={e.h}>
                <div className="pts">{e.pts}</div>
                <h4>{e.h}</h4>
                <p>{e.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="rw-rule" />

      <section className="rw-tint">
        <div className="rw-wrap">
          <p className="rw-eyebrow">The value loop</p>
          <h2>Where the money to reward you actually comes from.</h2>
          <p className="rw-lead">
            No ads. No selling your name. Restaurants pay for the one thing they&apos;ve never been
            able to see — how the decision to come was made. You made it, so you get a share.
          </p>
          <div className="rw-flow">
            {LOOP.map((n, i) => (
              <React.Fragment key={n.h}>
                {i > 0 && <div className="rw-arrow">{arrow}</div>}
                <div className={`rw-node${n.mid ? ' mid' : ''}`}>
                  <div className="k">{n.k}</div>
                  <h4>{n.h}</h4>
                  <p>{n.p}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <hr className="rw-rule" />

      <section>
        <div className="rw-wrap">
          <p className="rw-eyebrow">Redeem</p>
          <h2>Turn your monthly credit into things you&apos;d actually use.</h2>
          <p className="rw-lead">
            No credit card required. Each month you clear your goal, you unlock that month&apos;s
            credit — then redeem it as a gift card, or cash out to your bank, Venmo, or a prepaid
            Visa with Explorer or VIP.
          </p>
          <div className="rw-redeem">
            {REDEEM.map((r) => (
              <div className={`rw-rcard${r.soon ? ' soon' : ''}`} key={r.h}>
                <div className="k">{r.soon ? 'Coming soon' : 'Available'}</div>
                <h4>{r.h}</h4>
                <p>{r.p}</p>
              </div>
            ))}
          </div>
          <p className="rw-fineprint">
            Rewards unlock after an initial activity period — sooner on higher tiers — and each month
            you have to hit your tier&apos;s points goal to unlock that month&apos;s credit. Higher
            tiers need fewer points, earn faster, and VIP carries unused qualifying points forward.
            Transfer fees may apply depending on the method you choose. Points are aggregated and
            non-personal until redeemed.
          </p>
        </div>
      </section>

      <hr className="rw-rule" />

      <section className="rw-statement">
        <div className="rw-wrap">
          <p className="rw-eyebrow">Anonymous, then yours</p>
          <h2>You earned these before we knew your name.</h2>
          <p className="rw-lead">
            You don&apos;t need an account to start earning — your device quietly banks points as
            you explore. Signing up isn&apos;t joining something new. It&apos;s{' '}
            <strong className="ink">claiming what you already built</strong>, so it follows you across
            every phone and never resets.
          </p>
        </div>
      </section>

      <hr className="rw-rule" />

      <section className="rw-statement">
        <div className="rw-wrap">
          <p className="rw-eyebrow">Anonymous by design</p>
          <h2>You are not the product. The pattern is.</h2>
          <p className="rw-lead">
            What restaurants receive is the{' '}
            <strong className="ink">shape of demand in aggregate</strong> — never a person, never
            your name. Points are simply our half of that exchange: you help the industry see, and you
            earn for it.
          </p>
        </div>
      </section>

      <hr className="rw-rule" />

      <section>
        <div className="rw-wrap">
          <p className="rw-eyebrow">Where this goes</p>
          <h2 className="narrow">More ways to earn. More ways to get rewarded.</h2>
          <p className="rw-lead">
            Points are just the start. Here&apos;s what&apos;s on the way.
          </p>
          <div className="rw-chips">
            {FUTURE.map((f) => (
              <span className="rw-chip" key={f}>{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="rw-cta">
        <div className="rw-wrap">
          <p className="rw-eyebrow">{showAnonPointsLever ? 'Don’t leave them behind' : 'Start earning'}</p>
          <h2>
            {showAnonPointsLever
              ? `${points?.toLocaleString()} points are waiting. Claim them.`
              : 'The points you earn are yours. Come get them.'}
          </h2>
          <p className="rw-lead">
            {isAuthenticated
              ? 'Head to your points to see your balance, progress, and everything you can redeem.'
              : "It takes a minute, and everything you've already earned comes with you."}
          </p>
          <div className="rw-paths">
            <button type="button" className="rw-btn rw-primary" onClick={handleCTA}>
              {ctaLabel}
            </button>
            <a className="rw-btn rw-ghost" href={ROUTES.ABOUT}>How Resactly works</a>
          </div>
        </div>
      </section>

      <section className="rw-faq">
        <div className="rw-wrap">
          <PointsFaqSection />
        </div>
      </section>
    </div>
  )
}

/** Format a redeemable-credit cents value as a dollar string (no trailing .00 for whole dollars). */
const formatCredit = (cents: number): string => {
  const dollars = cents / 100
  return dollars % 1 === 0
    ? `$${dollars.toLocaleString()}`
    : `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const arrow = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
)

const EARN = [
  { pts: '+500', h: 'Scan a menu', p: 'Point your camera at a menu and we tell you what to order. Scanning a brand-new menu earns the most.' },
  { pts: '+25', h: 'Review', p: 'Say what you thought after. Honest signal is the most valuable kind — your first review earns a bonus.' },
  { pts: '+15', h: 'Save', p: 'Bookmark a spot for later. It tells the industry what real demand looks like — your first save earns extra.' },
  { pts: '+5', h: 'Rate a dish', p: 'Tell us about a dish you tried. Every rating sharpens what we recommend next.' },
]

const LOOP = [
  { k: 'You', h: 'Decide', p: 'Explore, compare, save, scan — the deciding, made visible.', mid: false },
  { k: 'Resactly', h: 'Intent Intelligence', p: 'Your actions, stripped of identity, become demand signal.', mid: true },
  { k: 'Restaurants', h: 'Subscribe', p: 'They pay to finally see how tables get chosen.', mid: false },
  { k: 'You, again', h: 'Get rewarded', p: 'A share of that value returns to you as points.', mid: false },
]

const REDEEM = [
  { h: 'Gift cards', p: 'Redeem for gift cards to places you already spend — free for everyone.', soon: false },
  { h: 'Cash out', p: 'Move it to your bank or Venmo. An Explorer & VIP perk.', soon: false },
  { h: 'Visa prepaid', p: 'Load a prepaid card and spend it anywhere. An Explorer & VIP perk.', soon: false },
  { h: 'Dining credit', p: 'Spend points directly at partner restaurants.', soon: true },
]

// Genuinely upcoming only — group bonuses, login streaks, referral rewards and tier perks are
// already live, so they no longer belong on the "what's next" list.
const FUTURE = [
  'Dining credit at partner restaurants',
  'Seasonal point drops',
  'A bigger rewards catalog',
]

const css = `
.rsly-rewards{
  --ground:#F1F0EB;--surface:#FBFAF7;--ink:#191510;--muted:#6C665C;--line:#D9D5CC;
  --signal:#DA4E1C;--signal-ink:#B23F14;--signal-wash:#DA4E1C14;
  --serif:'Fraunces',ui-serif,"New York","Iowan Old Style",Georgia,"Times New Roman",serif;
  --sans:'Geist Sans','Geist',-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,"Helvetica Neue",sans-serif;
  --mono:'JetBrains Mono',ui-monospace,"SF Mono","Cascadia Code",Menlo,monospace;
  background:var(--ground);color:var(--ink);font-family:var(--sans);font-size:17px;line-height:1.6;
  -webkit-font-smoothing:antialiased;overflow-x:hidden;
}
@media (prefers-color-scheme:dark){.rsly-rewards{
  --ground:#14110D;--surface:#1B1712;--ink:#EEE9E0;--muted:#9C9488;--line:#2C271F;
  --signal:#FF6C39;--signal-ink:#FF8256;--signal-wash:#FF6C3914;}}
.rsly-rewards *{box-sizing:border-box}
.rsly-rewards .rw-wrap{max-width:1120px;margin-inline:auto;padding-inline:clamp(1.25rem,5vw,3rem)}
.rsly-rewards section{padding-block:clamp(4rem,11vh,7.5rem);position:relative}
.rsly-rewards .rw-rule{height:1px;background:var(--line);border:0;margin:0}
.rsly-rewards h1,.rsly-rewards h2,.rsly-rewards h3{font-family:var(--serif);font-weight:500;text-wrap:balance;letter-spacing:-.015em}
.rsly-rewards h2{font-size:clamp(2rem,4.6vw,3.35rem);line-height:1.08;margin:0 0 1.1rem;max-width:20ch}
.rsly-rewards h2.narrow{max-width:16ch}
.rsly-rewards .rw-lead{font-size:clamp(1.08rem,1.7vw,1.32rem);color:var(--muted);max-width:62ch;line-height:1.5;margin:0}
.rsly-rewards .rw-lead .ink,.rsly-rewards .rw-lead strong{color:var(--ink);font-weight:600}
.rsly-rewards .rw-eyebrow{font-family:var(--mono);font-size:.72rem;letter-spacing:.2em;text-transform:uppercase;color:var(--muted);display:inline-flex;align-items:center;gap:.7rem;margin:0 0 1.4rem}
.rsly-rewards .rw-eyebrow::before{content:"";width:7px;height:7px;background:var(--signal);box-shadow:0 0 0 4px var(--signal-wash)}
.rsly-rewards .rw-btn{font-family:var(--sans);font-size:.95rem;font-weight:600;padding:.85rem 1.4rem;border-radius:2px;border:1px solid transparent;cursor:pointer;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:.5rem;min-height:44px;transition:transform .15s,background .15s,border-color .15s,opacity .15s}
.rsly-rewards .rw-primary{background:var(--signal);color:#fff}
.rsly-rewards .rw-primary:hover{transform:translateY(-1px);background:var(--signal-ink)}
.rsly-rewards .rw-ghost{background:transparent;color:inherit;border-color:currentColor;opacity:.82}
.rsly-rewards .rw-ghost:hover{opacity:1;transform:translateY(-1px)}
.rsly-rewards .rw-btn:focus-visible{outline:2px solid var(--signal);outline-offset:3px}
.rsly-rewards .rw-paths{display:flex;flex-wrap:wrap;gap:.8rem;margin-top:2rem}
.rsly-rewards .rw-hero{background:#100D0A;color:#EFE9DF;position:relative;overflow:hidden;padding-block:clamp(4.5rem,14vh,9rem);border-bottom:1px solid #241E17}
.rsly-rewards .rw-hero .rw-eyebrow{color:#B6AA97}
.rsly-rewards .rw-hero h1{font-size:clamp(2.4rem,5.8vw,4.6rem);line-height:1.05;margin:0 0 1.4rem;max-width:18ch;color:#F4EFE6}
.rsly-rewards .rw-hero h1 .warm{color:#FF7A47}
.rsly-rewards .rw-sub{font-family:var(--sans);font-size:clamp(1.05rem,1.7vw,1.28rem);color:#B6AA97;max-width:52ch;line-height:1.5;margin:0}
.rsly-rewards .rw-fine{font-family:var(--mono);font-size:.68rem;letter-spacing:.04em;color:#8A8277;margin:1.1rem 0 0}
.rsly-rewards .rw-count{display:flex;align-items:baseline;gap:.6rem;margin:0 0 1.4rem}
.rsly-rewards .rw-count .num{font-family:var(--serif);font-weight:500;font-size:clamp(3.6rem,13vw,7rem);line-height:.9;color:#FF7A47;font-variant-numeric:tabular-nums;letter-spacing:-.02em}
.rsly-rewards .rw-count .unit{font-family:var(--mono);font-size:.8rem;letter-spacing:.18em;text-transform:uppercase;color:#B6AA97}
.rsly-rewards .rw-prog{max-width:420px;margin:0 0 1.8rem}
.rsly-rewards .rw-prog .track{height:8px;border-radius:20px;background:#241E17;overflow:hidden}
.rsly-rewards .rw-prog .track>i{display:block;height:100%;border-radius:20px;background:linear-gradient(90deg,#FF6C39,#FF9366);transition:width .9s cubic-bezier(.2,.7,.2,1)}
.rsly-rewards .rw-prog .cap{font-family:var(--mono);font-size:.68rem;letter-spacing:.04em;color:#9C9488;margin:.7rem 0 0}
.rsly-rewards .rw-earn{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--line);border:1px solid var(--line);border-radius:4px;overflow:hidden;margin-top:2.6rem}
@media (max-width:820px){.rsly-rewards .rw-earn{grid-template-columns:repeat(2,1fr)}}
@media (max-width:460px){.rsly-rewards .rw-earn{grid-template-columns:1fr}}
.rsly-rewards .rw-card{background:var(--surface);padding:1.5rem 1.5rem;min-height:150px}
.rsly-rewards .rw-card .pts{font-family:var(--mono);font-size:.82rem;font-weight:600;letter-spacing:.02em;color:#fff;background:var(--signal);display:inline-block;padding:2px 9px;border-radius:20px}
.rsly-rewards .rw-card h4{font-family:var(--serif);font-weight:500;font-size:1.3rem;margin:.85rem 0 .35rem}
.rsly-rewards .rw-card p{margin:0;font-size:.9rem;color:var(--muted);line-height:1.45}
.rsly-rewards .rw-tint{background:var(--surface);border-block:1px solid var(--line)}
.rsly-rewards .rw-flow{display:grid;grid-template-columns:1fr auto 1fr auto 1fr auto 1fr;align-items:stretch;margin-top:2.6rem}
@media (max-width:900px){.rsly-rewards .rw-flow{grid-template-columns:1fr}.rsly-rewards .rw-arrow{transform:rotate(90deg);padding:.4rem 0}}
.rsly-rewards .rw-node{background:var(--ground);border:1px solid var(--line);border-radius:4px;padding:1.35rem 1.4rem}
.rsly-rewards .rw-tint .rw-node{background:var(--ground)}
.rsly-rewards .rw-node .k{font-family:var(--mono);font-size:.66rem;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.rsly-rewards .rw-node h4{font-family:var(--serif);font-weight:500;font-size:1.16rem;margin:.5rem 0 .35rem}
.rsly-rewards .rw-node p{margin:0;font-size:.86rem;color:var(--muted);line-height:1.45}
.rsly-rewards .rw-node.mid{border-color:var(--signal);box-shadow:0 0 0 1px var(--signal),0 12px 30px -18px var(--signal)}
.rsly-rewards .rw-node.mid .k{color:var(--signal-ink)}
.rsly-rewards .rw-arrow{display:flex;align-items:center;justify-content:center;padding:0 .8rem;color:var(--signal)}
.rsly-rewards .rw-arrow svg{width:24px;height:24px}
.rsly-rewards .rw-redeem{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--line);border:1px solid var(--line);border-radius:4px;overflow:hidden;margin-top:2.6rem}
@media (max-width:820px){.rsly-rewards .rw-redeem{grid-template-columns:repeat(2,1fr)}}
@media (max-width:460px){.rsly-rewards .rw-redeem{grid-template-columns:1fr}}
.rsly-rewards .rw-rcard{background:var(--surface);padding:1.5rem 1.5rem;min-height:150px}
.rsly-rewards .rw-rcard .k{font-family:var(--mono);font-size:.64rem;letter-spacing:.14em;text-transform:uppercase;color:var(--signal-ink)}
.rsly-rewards .rw-rcard.soon{opacity:.62}
.rsly-rewards .rw-rcard.soon .k{color:var(--muted)}
.rsly-rewards .rw-rcard h4{font-family:var(--serif);font-weight:500;font-size:1.28rem;margin:.7rem 0 .35rem}
.rsly-rewards .rw-rcard p{margin:0;font-size:.88rem;color:var(--muted);line-height:1.45}
.rsly-rewards .rw-fineprint{font-family:var(--mono);font-size:.68rem;letter-spacing:.02em;color:var(--muted);margin:1.6rem 0 0;max-width:60ch;line-height:1.6}
.rsly-rewards .rw-statement h2{max-width:18ch}
.rsly-rewards .rw-chips{display:flex;flex-wrap:wrap;gap:.7rem;margin-top:2.2rem}
.rsly-rewards .rw-chip{font-family:var(--mono);font-size:.74rem;letter-spacing:.04em;color:var(--ink);background:var(--surface);border:1px solid var(--line);border-radius:20px;padding:.55rem 1rem}
.rsly-rewards .rw-cta{background:#100D0A;color:#EFE9DF;text-align:center;border-top:1px solid #241E17}
.rsly-rewards .rw-cta h2{max-width:22ch;margin-inline:auto;color:#F4EFE6}
.rsly-rewards .rw-cta .rw-lead{margin-inline:auto;color:#B6AA97}
.rsly-rewards .rw-cta .rw-eyebrow{color:#B6AA97}
.rsly-rewards .rw-cta .rw-paths{justify-content:center}
.rsly-rewards .rw-faq{padding-block:clamp(2rem,6vh,4rem)}
.rsly-rewards .rw-faq .rw-wrap{max-width:900px}
@media (prefers-reduced-motion:reduce){.rsly-rewards *{animation:none!important;transition:none!important}}
`

export default RewardsInfoPage
